import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useBranch } from "~/contexts/BranchContext";
import { useTranslation } from "react-i18next";

const CurrencySwitcher: React.FC = () => {
  const { currencies, selectedCurrency, setSelectedCurrency, theme } =
    useBranch();
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const availableCurrencies = currencies ?? [];

  const isRTL = i18n.language === "ar";
  const menuAlignment = isRTL ? "right-0" : "left-0";
  const itemTextAlignment = isRTL ? "text-right" : "text-left";

  const selectedSymbol = useMemo(
    () => selectedCurrency?.symbol ?? availableCurrencies[0]?.symbol ?? "$",
    [availableCurrencies, selectedCurrency?.symbol],
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCurrencyChange = useCallback(
    (currencyId: string) => {
      const selected = availableCurrencies.find(
        (currency) => currency.id === currencyId,
      );
      if (!selected) {
        return;
      }

      setSelectedCurrency(selected);
      closeMenu();
    },
    [availableCurrencies, closeMenu, setSelectedCurrency],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeMenu, isOpen]);

  if (!availableCurrencies.length) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        type="button"
        className="bg-white/10 h-10 w-10 backdrop-blur-lg hover:bg-white/20 p-2 rounded-full text-white cursor-pointer transition-colors flex items-center justify-center"
        onClick={() => setIsOpen((prev) => !prev)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-label="Select currency"
      >
        <span className="font-semibold leading-none">{selectedSymbol}</span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={menuId}
            role="menu"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full mt-1 min-w-11 bg-white rounded-md shadow-lg z-50 overflow-hidden ${menuAlignment}`}
          >
            <div className="p-1">
              {availableCurrencies.map((currency) => {
                const isSelected = selectedCurrency?.id === currency.id;

                return (
                  <motion.button
                    key={currency.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    onClick={() => handleCurrencyChange(currency.id)}
                    className={`block w-full px-3 py-2 rounded-sm transition-colors text-sm ${itemTextAlignment} ${
                      isSelected
                        ? "text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    style={{
                      backgroundColor: isSelected
                        ? theme?.primaryColor ?? "#1b4d3e"
                        : "transparent",
                    }}
                    whileHover={{ x: isRTL ? -2 : 2 }}
                  >
                    <span className="font-semibold">{currency.symbol}</span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CurrencySwitcher;
